import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useAuth } from "../../auth/hooks";
import {
  createExtractJob,
  listExtractJobs,
  downloadExtractJob,
  triggerBrowserDownload,
} from "../services/extractAPI";
import {
  CreateExtractJobPayload,
  CreateExtractJobResponse,
  ExtractJobDTO,
  ExtractJobListResponse,
} from "../types/extract.types";

const QUERY_KEY = "extractJobs";

/**
 * Polls the jobs list every 10 s only while at least one job is in flight
 * (pending or running). Once everything settles we drop back to manual
 * refetch — saves the backend ~6 polls/min/account on idle accounts.
 */
export function useExtractJobsList() {
  const { setHeaderAuthToRequest, getUserIsAuthenticated } = useAuth();
  setHeaderAuthToRequest();

  return useQuery<ExtractJobListResponse>(QUERY_KEY, listExtractJobs, {
    enabled: getUserIsAuthenticated(),
    refetchInterval: (data) => {
      if (!data || !data.jobs) return false;
      const hasActive = data.jobs.some(
        (j) => j.status === "pending" || j.status === "running"
      );
      return hasActive ? 10_000 : false;
    },
    refetchOnWindowFocus: true,
  });
}

/**
 * Returns the in-flight job for the current account, if any. The form
 * disables the submit button while this is non-null so the user is not
 * surprised by the 409 from the backend's "1 active per account" guard.
 */
export function useActiveExtractJob(): ExtractJobDTO | null {
  const { data } = useExtractJobsList();
  return useMemo(() => {
    if (!data?.jobs) return null;
    return (
      data.jobs.find(
        (j) => j.status === "pending" || j.status === "running"
      ) ?? null
    );
  }, [data]);
}

export function useCreateExtractJob() {
  const { setHeaderAuthToRequest } = useAuth();
  setHeaderAuthToRequest();
  const qc = useQueryClient();

  return useMutation<CreateExtractJobResponse, Error, CreateExtractJobPayload>(
    (payload) => createExtractJob(payload),
    {
      onSuccess: () => {
        qc.invalidateQueries(QUERY_KEY);
      },
    }
  );
}

/**
 * Downloads the CSV via the authenticated endpoint and triggers a save
 * dialog. We can't use a plain <a href> because we need to send the
 * Authorization header, so we fetch+blob then synthesise the click.
 */
export function useDownloadExtractJob() {
  const { setHeaderAuthToRequest } = useAuth();
  setHeaderAuthToRequest();

  return useMutation<void, Error, ExtractJobDTO>(async (job) => {
    const blob = await downloadExtractJob(job.id);
    const date = job.createdAt ? job.createdAt.slice(0, 10) : "extract";
    const name = `extract_${job.extractId || "export"}_${date}.csv`;
    triggerBrowserDownload(blob, name);
  });
}
